import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { loginRestaurant, getMyInfo, getRestaurant, logoutWebSession } from 'services/authService';
import { setCookie, getCookie, removeCookie } from 'utils/cookies';

const AuthContext = createContext(null);

function parseJsonCookie(name) {
  try { return JSON.parse(getCookie(name)); } catch { return null; }
}

export function AuthProvider({ children }) {
  const [user,           setUser]           = useState(() => parseJsonCookie('authUser'));
  const [restaurant,     setRestaurant]     = useState(() => parseJsonCookie('authRestaurant'));
  // isAuthenticated is driven by a plain (non-httpOnly) flag cookie only — the real JWT
  // lives in the httpOnly `web_access_token` cookie set by the server and is never
  // readable by JavaScript.
  const [isAuthenticated, setIsAuthenticated] = useState(() => getCookie('_sid') === '1');

  // On mount, re-hydrate user/restaurant from the API if the session flag says we're logged in
  // but the user cookie is missing (e.g. cookie cleared by browser settings).
  useEffect(() => {
    if (isAuthenticated && !user) {
      getMyInfo()
        .then((data) => {
          const usr = data.user || data.data || data;
          setUser(usr);
          setCookie('authUser', JSON.stringify(usr), 7);
        })
        .catch(() => {
          // Token is gone — force logout
          setIsAuthenticated(false);
          removeCookie('_sid');
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    // Authentication is carried only by the httpOnly cookies set by the server.
    const data = await loginRestaurant(email, password);
    const usr  = data.user  || data.data?.user  || null;
    const rest = data.restaurant || data.data?.restaurant || null;
    const restaurantId = data.restaurant_id || usr?.restaurant_id;

    // Persist non-sensitive metadata in regular cookies (7 days)
    if (usr)  setCookie('authUser',       JSON.stringify(usr),  7);
    if (rest) setCookie('authRestaurant', JSON.stringify(rest), 7);
    // Session flag — just a boolean, not the token
    setCookie('_sid', '1', 7);

    setUser(usr);
    setRestaurant(rest);
    setIsAuthenticated(true);

    // Load restaurant details if not included in login response
    if (!rest && restaurantId) {
      getRestaurant(restaurantId)
        .then((r) => {
          const rd = r.data || r;
          setRestaurant(rd);
          setCookie('authRestaurant', JSON.stringify(rd), 7);
        })
        .catch(() => {});
    }

    return { user: usr, restaurant: rest };
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutWebSession();
    } catch {
      // Client-side cleanup below still guarantees the dashboard leaves the session.
    }

    // Clear all client-side session data
    removeCookie('authUser');
    removeCookie('authRestaurant');
    removeCookie('_sid');
    // The httpOnly web_access_token cookie can only be cleared server-side.
    // Redirecting to sign-in is enough — the expired/cleared token will cause 401s.
    setUser(null);
    setRestaurant(null);
    setIsAuthenticated(false);
    window.location.href = '/auth/sign-in';
  }, []);

  const updateRestaurant = useCallback((data) => {
    setRestaurant(data);
    setCookie('authRestaurant', JSON.stringify(data), 7);
  }, []);

  const restaurantId = restaurant?.id || user?.restaurant_id;

  return (
    <AuthContext.Provider value={{
      user, restaurant, restaurantId,
      login, logout, updateRestaurant,
      isAuthenticated,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
